import React, { useState, useRef, useEffect } from "react";

const OSShell = ({
  children,
  windows = [],
  setWindows,
  zIndex = 1000,
  setZIndex,
  onCloseWindow,
  onMinimizeWindow,
  onMaximizeWindow,
  onFocusWindow,
  onPositionChange,
  onResizeWindow,
  onRestoreWindow,
  theme,
  osStyle,
  onOsStyleChange,
  themes,
  weatherLocation,
  setWeatherLocation,
  renderAppComponent,
  focusedWindowId,
}) => {
  const shellRef = useRef(null);
  const [isStartMenuOpen, setIsStartMenuOpen] = useState(false);
  const startMenuRef = useRef(null);

  // Close Start menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (startMenuRef.current && !startMenuRef.current.contains(event.target)) {
        setIsStartMenuOpen(false);
      }
    };

    if (isStartMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isStartMenuOpen]);

  // Close a window
  const closeWindow = (id) => {
    onCloseWindow(id);
  };

  // Minimize a window
  const minimizeWindow = (id) => {
    onMinimizeWindow(id);
  };

  // Maximize a window
  const maximizeWindow = (id) => {
    onMaximizeWindow(id);
  };

  // Bring window to front
  const focusWindow = (id) => {
    onFocusWindow(id);
  };

  // Update window position
  const updateWindowPosition = (id, x, y) => {
    onPositionChange(id, x, y);
  };

  return (
    <div
      ref={shellRef}
      className="w-full h-screen overflow-hidden flex flex-col"
      style={{ background: theme.desktop }}
    >
      {/* Desktop - Main content area */}
      <div className="flex-1 relative overflow-hidden">
        {children}
      </div>

      {/* Windows Layer */}
      <div
        className="absolute inset-0 top-0 left-0 right-0 pointer-events-none"
        style={{ 
          zIndex: 500,
          bottom: "28px",
          pointerEvents: "none"
        }}
      >
        {windows.map((window) => (
          <div
            key={window.id}
            style={{ pointerEvents: "auto" }}
          >
            <Window95
              window={window}
              onClose={closeWindow}
              onMinimize={minimizeWindow}
              onMaximize={maximizeWindow}
              onFocus={focusWindow}
              onPositionChange={updateWindowPosition}
              onResize={onResizeWindow}
              theme={theme}
            >
              {renderAppComponent(window.appId)}
            </Window95>
          </div>
        ))}
      </div>

      {/* Taskbar */}
      <div
        className="w-full flex items-center border-t-2 relative"
        style={{
          background: theme.taskbar,
          borderTopColor: theme.taskbarBorder,
          borderBottomColor: theme.border.right,
          height: "28px",
          zIndex: 9999,
        }}
      >
        <div className="flex items-center gap-2 relative" ref={startMenuRef}>
          <button
            onClick={() => setIsStartMenuOpen(!isStartMenuOpen)}
            className="px-2 font-bold text-xs border-2"
            style={{
              background: theme.window,
              borderTopColor: isStartMenuOpen ? theme.border.right : theme.border.top,
              borderLeftColor: isStartMenuOpen ? theme.border.right : theme.border.left,
              borderRightColor: isStartMenuOpen ? theme.border.left : theme.border.right,
              borderBottomColor: isStartMenuOpen ? theme.border.left : theme.border.bottom,
              color: theme.taskbarText,
              cursor: 'pointer',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            [Start]
          </button>

          {/* Start Menu Flyout */}
          {isStartMenuOpen && (
            <div
              className="absolute bottom-full left-0 border-2 mb-1"
              style={{
                background: theme.window,
                borderTopColor: theme.border.top,
                borderLeftColor: theme.border.left,
                borderRightColor: theme.border.right,
                borderBottomColor: theme.border.bottom,
                boxShadow: `inset 1px 1px 0 ${theme.border.top}, inset -1px -1px 0 ${theme.border.right}`,
                minWidth: '280px',
              }}
            >
              <div className="py-1">
                {/* Display Section */}
                <div
                  className="px-3 py-1 text-xs font-bold"
                  style={{ color: theme.taskbarText, borderBottom: `1px solid ${theme.border.right}` }}
                >
                  Display
                </div>
                <select
                  value={osStyle}
                  onChange={(e) => {
                    onOsStyleChange(e.target.value);
                    setIsStartMenuOpen(false);
                  }}
                  className="w-full px-3 py-1 text-xs border-0"
                  style={{
                    background: theme.window,
                    color: theme.taskbarText,
                    cursor: 'pointer',
                  }}
                >
                  {Object.entries(themes).map(([key, t]) => (
                    <option key={key} value={key}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* System Section */}
              <div style={{ borderTop: `1px solid ${theme.border.right}`, paddingTop: '4px', marginTop: '4px' }}>
                <button
                  onClick={() => {
                    // Emit event to launch SystemInfo
                    window.dispatchEvent(new CustomEvent('launchApp', { detail: { id: 'systeminfo' } }));
                    setIsStartMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-xs hover:opacity-80 transition-opacity"
                  style={{ 
                    background: theme.window,
                    color: theme.taskbarText,
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  System Info...
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex-1 flex gap-1 ml-2 overflow-x-auto">
          {windows.map((window) => (
            <button
              key={window.id}
              onClick={() => {
                // Use restore function for minimized windows
                if (window.isMinimized) {
                  onRestoreWindow(window.id);
                } else {
                  onFocusWindow(window.id);
                }
              }}
              className="px-2 py-1 text-xs border-2 font-bold whitespace-nowrap"
              style={{
                background: window.id === focusedWindowId ? "#0078d4" : theme.taskbar,
                color: window.id === focusedWindowId ? "#ffffff" : theme.taskbarText,
                borderTopColor: theme.border.top,
                borderLeftColor: theme.border.left,
                borderRightColor: theme.border.right,
                borderBottomColor: theme.border.bottom,
                color: theme.taskbarText,
              }}
              title={window.isMinimized ? "Restore" : "Focus"}
            >
              {window.title}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Windows 95 Window Component
const Window95 = ({
  window: windowData,
  onClose,
  onMinimize,
  onMaximize,
  onFocus,
  onPositionChange,
  onResize,
  theme,
  children,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const windowRef = useRef(null);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    onFocus(windowData.id);
    setDragOffset({
      x: e.clientX - windowData.x,
      y: e.clientY - windowData.y,
    });
  };

  const handleResizeStart = (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    setIsResizing(true);
    onFocus(windowData.id);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: windowData.width,
      height: windowData.height,
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      onPositionChange(windowData.id, e.clientX - dragOffset.x, e.clientY - dragOffset.y);
    } else if (isResizing) {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      const newWidth = Math.max(300, resizeStart.width + deltaX);
      const newHeight = Math.max(200, resizeStart.height + deltaY);
      onResize(windowData.id, newWidth, newHeight);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  React.useEffect(() => {
    if (isDragging || isResizing) {
      globalThis.window.addEventListener("mousemove", handleMouseMove);
      globalThis.window.addEventListener("mouseup", handleMouseUp);
      return () => {
        globalThis.window.removeEventListener("mousemove", handleMouseMove);
        globalThis.window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset, resizeStart, windowData.id]);

  const isHidden = windowData.isMinimized;

  return (
    <div
      ref={windowRef}
      onMouseDown={() => onFocus(windowData.id)}
      className={`absolute border-2 flex flex-col ${
        isHidden ? "hidden" : ""
      }`}
      style={{
        left: `${windowData.x}px`,
        top: `${windowData.y}px`,
        width: windowData.isMaximized ? "100%" : `${windowData.width}px`,
        height: windowData.isMaximized ? "calc(100% - 28px)" : `${windowData.height}px`,
        background: theme.window,
        borderTopColor: theme.border.top,
        borderLeftColor: theme.border.left,
        borderRightColor: theme.border.right,
        borderBottomColor: theme.border.bottom,
        zIndex: windowData.zIndex,
        boxShadow: `inset 1px 1px 0 ${theme.border.top}, inset -1px -1px 0 ${theme.border.right}`,
      }}
    >
      {/* Title Bar */}
      <div
        className="flex items-center justify-between px-1 py-0.5 select-none cursor-move"
        style={{
          background: theme.title,
          height: "20px",
        }}
        onMouseDown={handleMouseDown}
      >
        <span className="text-white text-xs font-bold">{windowData.title}</span>
        <div className="flex gap-0.5">
          <WindowButton
            onClick={() => onMinimize(windowData.id)}
            title="Minimize"
            theme={theme}
          >
            _
          </WindowButton>
          <WindowButton
            onClick={() => onMaximize(windowData.id)}
            title="Maximize"
            theme={theme}
          >
            □
          </WindowButton>
          <WindowButton
            onClick={() => onClose(windowData.id)}
            title="Close"
            theme={theme}
          >
            ×
          </WindowButton>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto bg-white">
        {children}
      </div>

      {/* Resize Handle */}
      {!windowData.isMaximized && (
        <div
          onMouseDown={handleResizeStart}
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: "16px",
            height: "16px",
            cursor: "nwse-resize",
            background: `linear-gradient(135deg, transparent 0%, transparent 50%, ${theme.window} 50%, ${theme.window} 100%)`,
            borderLeft: `1px solid ${theme.border.top}`,
            borderTop: `1px solid ${theme.border.top}`,
          }}
        />
      )}
    </div>
  );
};

// Window95 Button Component
const WindowButton = ({ onClick, title, theme, children }) => {
  const [isPressed, setIsPressed] = useState(false);

  return (
    <button
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      onClick={onClick}
      title={title}
      className="w-6 h-5 text-xs font-bold flex items-center justify-center"
      style={{
        background: theme.window,
        borderTopColor: isPressed ? theme.border.right : theme.border.top,
        borderLeftColor: isPressed ? theme.border.right : theme.border.left,
        borderRightColor: isPressed ? theme.border.left : theme.border.right,
        borderBottomColor: isPressed ? theme.border.left : theme.border.bottom,
        borderWidth: "2px",
      }}
    >
      {children}
    </button>
  );
};

export { OSShell, Window95 };
